/**
 * `SearchReplaceExtension` — Tiptap extension powering the in-editor
 * Find / Replace bar (Ctrl/Cmd+F).
 *
 * Decoration-only for the *highlighting* side — same pattern as
 * `quoteHighlightExtension` and `entityDecoratorExtension`: a ProseMirror
 * Plugin holds the search state + a derived `DecorationSet` and never mutates
 * the document to paint matches. Replacement, by contrast, DOES mutate the
 * doc (that's the point) — it dispatches real `tr.replaceWith` transactions.
 *
 * Plugin state shape:
 *
 *   { searchTerm: string, caseSensitive: boolean,
 *     matches: { from, to }[], activeIndex: number }
 *
 *   - `matches` is recomputed whenever the search term / case flag changes
 *     (via a `setMeta` from the `setSearch` command) OR whenever the doc
 *     changes (a content edit could add/remove matches). All positions are
 *     ABSOLUTE doc positions, mapped from text-node offsets the same way the
 *     sister decorators do (`doc.descendants`, walk text nodes, accumulate).
 *   - `activeIndex` points at the "current" match — the one rendered with the
 *     stronger highlight + the one Next/Prev cycle through. Clamped into range
 *     on every recompute so it never dangles.
 *
 * Two decoration classes (styled in richTextEditor.css):
 *   - `.search-match`        — every match (soft wash)
 *   - `.search-match--active`— the active match (stronger wash)
 *
 * Commands exposed to the React bar (FindReplaceBar.tsx):
 *   - setSearch(term, caseSensitive) — set the term + recompute matches
 *   - nextMatch() / prevMatch()      — move activeIndex (wraps), scroll into view
 *   - replaceCurrent(replacement)    — replace the active match, advance
 *   - replaceAll(replacement)        — replace every match (END→START so
 *                                      earlier positions stay valid)
 *   - clearSearch()                  — empty term, drop all decorations
 *
 * Matching is LITERAL — the search term is treated as a plain string, never a
 * regex. We use `String.prototype.indexOf` over a flattened text buffer, so
 * regex-special characters (`.`, `*`, `(`, `[`, …) are matched verbatim and
 * can never blow up. (No RegExp is constructed at all.)
 */

import { Extension } from '@tiptap/react';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import type { Node as PmNode } from '@tiptap/pm/model';

/** A single match's absolute doc range. */
export interface SearchMatch {
  from: number;
  to: number;
}

/** Plugin state — also the shape React reads back via `getSearchState`. */
export interface SearchReplaceState {
  searchTerm: string;
  caseSensitive: boolean;
  matches: SearchMatch[];
  activeIndex: number;
}

/** Meta payload for the `setSearch` command. */
interface SetSearchMeta {
  type: 'setSearch';
  searchTerm: string;
  caseSensitive: boolean;
}

/** Meta payload for active-match movement. */
interface MoveMeta {
  type: 'move';
  /** New active index (already wrapped into range by the command). */
  activeIndex: number;
}

type SearchMeta = SetSearchMeta | MoveMeta;

export const searchReplacePluginKey = new PluginKey<SearchReplaceState>(
  'searchReplace',
);

const EMPTY_STATE: SearchReplaceState = {
  searchTerm: '',
  caseSensitive: false,
  matches: [],
  activeIndex: 0,
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    searchReplace: {
      /** Set the search term + case flag; recomputes matches + decorations. */
      setSearch: (searchTerm: string, caseSensitive: boolean) => ReturnType;
      /** Clear the search (empty term → no highlights, counter 0). */
      clearSearch: () => ReturnType;
      /** Advance the active match (wraps to first), scrolling it into view. */
      nextMatch: () => ReturnType;
      /** Step the active match back (wraps to last), scrolling it into view. */
      prevMatch: () => ReturnType;
      /** Replace the active match with `replacement`, then advance. */
      replaceCurrent: (replacement: string) => ReturnType;
      /** Replace every match with `replacement` (iterating END→START). */
      replaceAll: (replacement: string) => ReturnType;
    };
  }
}

export const SearchReplaceExtension = Extension.create({
  name: 'searchReplace',

  addProseMirrorPlugins() {
    return [
      new Plugin<SearchReplaceState>({
        key: searchReplacePluginKey,
        state: {
          init: () => EMPTY_STATE,
          apply(tr, prev): SearchReplaceState {
            const meta = tr.getMeta(searchReplacePluginKey) as
              | SearchMeta
              | undefined;

            // 1. A `setSearch` meta → new term/flag → recompute from scratch.
            if (meta?.type === 'setSearch') {
              const matches = computeMatches(
                tr.doc,
                meta.searchTerm,
                meta.caseSensitive,
              );
              return {
                searchTerm: meta.searchTerm,
                caseSensitive: meta.caseSensitive,
                // Reset to the first match on a fresh search.
                activeIndex: matches.length > 0 ? 0 : 0,
                matches,
              };
            }

            // 2. A `move` meta → just move the active highlight (no recompute).
            if (meta?.type === 'move') {
              return { ...prev, activeIndex: meta.activeIndex };
            }

            // 3. No relevant meta. If the doc changed, recompute matches (an
            //    edit — including our own replace transactions — can add or
            //    remove matches and shift positions). Otherwise keep state.
            if (!tr.docChanged) return prev;
            if (prev.searchTerm.length === 0) return prev;
            const matches = computeMatches(
              tr.doc,
              prev.searchTerm,
              prev.caseSensitive,
            );
            const activeIndex = clampIndex(prev.activeIndex, matches.length);
            return { ...prev, matches, activeIndex };
          },
        },
        props: {
          decorations(state) {
            const pluginState = searchReplacePluginKey.getState(state);
            if (pluginState === undefined) return DecorationSet.empty;
            const { matches, activeIndex, searchTerm } = pluginState;
            if (searchTerm.length === 0 || matches.length === 0) {
              return DecorationSet.empty;
            }
            const decorations = matches.map((m, i) =>
              Decoration.inline(m.from, m.to, {
                class:
                  i === activeIndex
                    ? 'search-match search-match--active'
                    : 'search-match',
              }),
            );
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setSearch:
        (searchTerm: string, caseSensitive: boolean) =>
        ({ tr, dispatch }) => {
          if (dispatch !== undefined) {
            const meta: SetSearchMeta = {
              type: 'setSearch',
              searchTerm,
              caseSensitive,
            };
            dispatch(tr.setMeta(searchReplacePluginKey, meta));
          }
          return true;
        },

      clearSearch:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch !== undefined) {
            const meta: SetSearchMeta = {
              type: 'setSearch',
              searchTerm: '',
              caseSensitive: false,
            };
            dispatch(tr.setMeta(searchReplacePluginKey, meta));
          }
          return true;
        },

      nextMatch:
        () =>
        ({ state, tr, dispatch, view }) => {
          const s = searchReplacePluginKey.getState(state);
          if (s === undefined || s.matches.length === 0) return false;
          const activeIndex = (s.activeIndex + 1) % s.matches.length;
          if (dispatch !== undefined) {
            const meta: MoveMeta = { type: 'move', activeIndex };
            dispatch(tr.setMeta(searchReplacePluginKey, meta));
            scrollMatchIntoView(view, s.matches[activeIndex]);
          }
          return true;
        },

      prevMatch:
        () =>
        ({ state, tr, dispatch, view }) => {
          const s = searchReplacePluginKey.getState(state);
          if (s === undefined || s.matches.length === 0) return false;
          const activeIndex =
            (s.activeIndex - 1 + s.matches.length) % s.matches.length;
          if (dispatch !== undefined) {
            const meta: MoveMeta = { type: 'move', activeIndex };
            dispatch(tr.setMeta(searchReplacePluginKey, meta));
            scrollMatchIntoView(view, s.matches[activeIndex]);
          }
          return true;
        },

      replaceCurrent:
        (replacement: string) =>
        ({ state, tr, dispatch, view }) => {
          const s = searchReplacePluginKey.getState(state);
          if (s === undefined || s.matches.length === 0) return false;
          const target = s.matches[s.activeIndex];
          if (target === undefined) return false;
          if (dispatch !== undefined) {
            // Replace the active match's range with the replacement text.
            // The plugin's `apply` sees `tr.docChanged` and recomputes
            // matches against the new doc; we keep the same activeIndex so
            // focus lands on the match that now occupies this slot (or is
            // clamped to the last match if we replaced the final one).
            applyReplace(tr, target.from, target.to, replacement);
            dispatch(tr);
            // After the doc mutates the plugin state recomputes; scroll the
            // (now-current) active match into view on the next tick so we
            // read the post-transaction positions.
            scrollActiveIntoViewDeferred(view);
          }
          return true;
        },

      replaceAll:
        (replacement: string) =>
        ({ state, tr, dispatch }) => {
          const s = searchReplacePluginKey.getState(state);
          if (s === undefined || s.matches.length === 0) return false;
          if (dispatch !== undefined) {
            // Iterate from the END of the doc backward so each replacement's
            // position shift never invalidates the not-yet-processed earlier
            // matches (their from/to are all lower than the range we're
            // editing). Doing it forward would require remapping every
            // remaining match through the transaction after each edit.
            for (let i = s.matches.length - 1; i >= 0; i--) {
              const m = s.matches[i];
              applyReplace(tr, m.from, m.to, replacement);
            }
            dispatch(tr);
          }
          return true;
        },
    };
  },
});

/**
 * Read the current plugin state for React. Returns the EMPTY_STATE shape when
 * the plugin hasn't initialised (defensive — the editor always has it once
 * mounted).
 */
export function getSearchState(view: EditorView): SearchReplaceState {
  return searchReplacePluginKey.getState(view.state) ?? EMPTY_STATE;
}

/** Clamp `index` into [0, length-1]; returns 0 for an empty list. */
function clampIndex(index: number, length: number): number {
  if (length === 0) return 0;
  if (index < 0) return 0;
  if (index >= length) return length - 1;
  return index;
}

/**
 * Replace the doc range [from, to) with literal `replacement` text on the
 * given transaction. Uses the doc's schema text node so marks at the boundary
 * aren't inherited in a surprising way. An empty `replacement` deletes the
 * range. Mutates `tr` in place (the caller dispatches once after looping).
 */
function applyReplace(
  tr: Transaction,
  from: number,
  to: number,
  replacement: string,
): void {
  if (replacement.length === 0) {
    tr.delete(from, to);
  } else {
    tr.replaceWith(from, to, tr.doc.type.schema.text(replacement));
  }
}

/**
 * Walk every text node in the doc, flattening into one string while tracking
 * the absolute doc position of each character, then find ALL (non-overlapping)
 * occurrences of `term`. Returns absolute-position ranges.
 *
 * Mirrors the text-walking approach in quoteHighlightExtension's
 * `findExactInDoc`, generalised to (a) find every occurrence and (b) NOT
 * collapse whitespace — Find/Replace wants exact literal matches so the
 * replacement maps cleanly onto what the user sees.
 *
 * A block boundary between text nodes emits a `\n` separator so a term never
 * matches ACROSS two nodes (the separator can't appear inside a search term
 * the user types into a single-line input, and even if pasted, a match that
 * straddles the synthetic newline would map to a non-contiguous doc range we
 * couldn't cleanly replace).
 */
function computeMatches(
  doc: PmNode,
  term: string,
  caseSensitive: boolean,
): SearchMatch[] {
  if (term.length === 0) return [];

  let flat = '';
  // posMap[i] = absolute doc position of flat[i]. Separators get a sentinel
  // position that no real match can start/end inside (we never index it as a
  // boundary because the separator char itself won't be part of `term`).
  const posMap: number[] = [];

  doc.descendants((node, pos) => {
    if (!node.isText) {
      // Emit a separator between blocks so matches can't span nodes.
      if (flat.length > 0 && flat[flat.length - 1] !== '\n') {
        flat += '\n';
        posMap.push(pos);
      }
      return true;
    }
    const raw = node.text ?? '';
    for (let i = 0; i < raw.length; i++) {
      flat += raw[i];
      posMap.push(pos + i);
    }
    return true;
  });

  const haystack = caseSensitive ? flat : flat.toLowerCase();
  const needle = caseSensitive ? term : term.toLowerCase();

  const matches: SearchMatch[] = [];
  let searchFrom = 0;
  for (;;) {
    const idx = haystack.indexOf(needle, searchFrom);
    if (idx < 0) break;
    const endExclusive = idx + needle.length;
    const fromDocPos = posMap[idx];
    // `to` is the doc position one past the last matched char: the doc pos of
    // the char after the match, or last-matched-char-pos + 1 at doc end.
    const toDocPos =
      endExclusive < posMap.length
        ? posMap[endExclusive]
        : posMap[posMap.length - 1] + 1;
    if (
      fromDocPos !== undefined &&
      toDocPos !== undefined &&
      toDocPos > fromDocPos &&
      // Reject any match that crossed a synthetic separator (its mapped
      // doc-range length wouldn't equal the literal match length).
      toDocPos - fromDocPos === needle.length
    ) {
      matches.push({ from: fromDocPos, to: toDocPos });
    }
    // Advance past this occurrence (non-overlapping). Guard zero-length.
    searchFrom = idx + Math.max(needle.length, 1);
  }
  return matches;
}

/**
 * Scroll a specific match range into view + place a (collapsed) text selection
 * at its start so ProseMirror's own `scrollIntoView` machinery can take over
 * if the consumer dispatches one. Best-effort; silently no-ops on bad pos.
 */
function scrollMatchIntoView(
  view: EditorView | undefined,
  match: SearchMatch | undefined,
): void {
  if (view === undefined || match === undefined) return;
  // Defer one tick so the move-meta transaction has applied (decoration's
  // active class lands first, then we scroll to it).
  setTimeout(() => {
    scrollDocPosIntoView(view, match.from);
  }, 0);
}

/** After a replace, scroll whatever is now the active match into view. */
function scrollActiveIntoViewDeferred(view: EditorView | undefined): void {
  if (view === undefined) return;
  setTimeout(() => {
    const s = getSearchState(view);
    const active = s.matches[s.activeIndex];
    if (active !== undefined) {
      scrollDocPosIntoView(view, active.from);
    }
  }, 0);
}

/**
 * Scroll the nearest scrollable ancestor so doc position `pos` is roughly
 * centred. Same approach as quoteHighlightExtension's scroll helper — uses
 * `coordsAtPos` so it doesn't depend on decoration DOM having painted.
 */
function scrollDocPosIntoView(view: EditorView, pos: number): void {
  let coords: { top: number; bottom: number; left: number; right: number };
  try {
    coords = view.coordsAtPos(Math.min(pos, view.state.doc.content.size));
  } catch {
    return;
  }
  const scrollParent = findScrollableAncestor(view.dom);
  if (scrollParent === null) return;
  const containerRect = scrollParent.getBoundingClientRect();
  const offsetWithinContainer = coords.top - containerRect.top;
  const desiredTop =
    scrollParent.scrollTop +
    offsetWithinContainer -
    scrollParent.clientHeight / 2;
  scrollParent.scrollTo({ top: Math.max(0, desiredTop), behavior: 'smooth' });
}

/** Walk up to the first ancestor with overflow-y auto/scroll. */
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = el.parentElement;
  while (current !== null) {
    const style = window.getComputedStyle(current);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}
